import React, { useContext, useState } from 'react';
import CustomDialog from '../../custom-dialog/custom-dialog';
import {
  DialogTitle,
  DialogContent,
  Button,
  CircularProgress,
  DialogActions,
  Stack,
  Typography,
} from '@mui/material';
import { LinkIcon } from 'lucide-react';
import { format } from 'date-fns';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import CopyLinkButton from '../../copy-link-button/copy-link-button';
import CustomerWithNavigate from '../../customer-with-navigate/customer-with-navigate';
import GenericTable from '../../generic-table/generic-table';
import OrderNotes from '../../order-notes/order-notes';
import { IconWithText } from '../../styles/components';
import { showDeliveryState } from '../../styles/order-states.style';
import { showWorkflow } from '../../styles/workflow-display';
import { AlertsData, AlertType } from './alerts';
import { useSnackBar } from '../../../../contexts/snackbar';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import axios from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { categorizeOrderAlerts, formatDaysLate } from './utils';
import {
  Cancel,
  House,
  NotificationsPaused,
  Storefront,
} from '@mui/icons-material';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import { showPickupTaskState } from '../../styles/pickup-task.styles';
import { displayLocation } from '../../../../utils/locations';
import { Workflow } from '../../../../enums/orders';
import PickupTaskDialog from '../../../delivery-company-components/pickup-tasks/pickup-task-dialog';

interface PickupsDialog {
  onClose: () => void;
  title: string;
  tasks: IPickupTask[];
}
function PickupsDialog({ onClose, title, tasks }: PickupsDialog) {
  const { showMessage } = useSnackBar();
  const { config } = useContext(AuthContext);
  const [editingTask, setEditingTask] = useState<IPickupTask | null>(null);
  return (
    <CustomDialog open={true} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <GenericTable<IPickupTask>
          sourceView="pickups-dialog"
          items={tasks}
          selectedItems={new Map()}
          isLoading={false}
          fullWidth
          columns={[
            {
              id: 'task_id',
              header: <p>Task ID</p>,
              display_body: (task) => (
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => setEditingTask(task)}
                >
                  <b>
                    <u>{task.id}</u>
                  </b>
                </div>
              ),
            },
            {
              id: 'creation_date',
              header: <p>Created At</p>,
              display_body: (task) => (
                <p>
                  {format(new Date(task.creation_date ?? ''), 'dd-MM-yyyy')}

                  <br />
                  {format(new Date(task.creation_date ?? ''), 'HH:mm:ss')}
                </p>
              ),
            },
            {
              id: 'workflow',
              header: <p>Workflow</p>,
              display_body: (task) => showWorkflow(task.workflow, false, undefined, ""),
            },
            {
              id: 'Pickup From',
              header: <p>Pickup From</p>,
              display_body: (task) => {
                if (task.workflow === Workflow.STANDARD)
                  return (
                    <>
                      <Storefront color="primary" /> <br />{' '}
                      <a href={`/merchant/${task.merchant?.id}`}>
                        <b>{task.merchant?.name}</b>
                        <br />
                        {displayLocation(task.merchant_location)}
                      </a>{' '}
                    </>
                  );
                else
                  return (
                    <>
                      {' '}
                      <House color="primary" /> <br />
                      <CustomerWithNavigate customer={task.customer!} />
                      <br />
                      {displayLocation(task.customer_location)}{' '}
                    </>
                  );
              },
            },
            {
              id: 'task_state',
              header: <p>Status</p>,
              display_body: (task) => (
                <>{showPickupTaskState(task.status, task.driver)}</>
              ),
            },
            {
              id: 'package count',
              header: <p># Packages</p>,
              display_body: (task) => <p>{task.package_count}</p>,
            },
            {
              header: <b>Delay</b>,
              id: 'alert_explanation',
              display_body: (task) => (
                <b>{formatDaysLate(task.creation_date!)}</b>
              ),
            },
          ]}
          getItemUID={(task) => task.id}
        />
      </DialogContent>
      {editingTask && (
        <PickupTaskDialog
          taskID={editingTask.id}
          onClose={() => setEditingTask(null)}
        />
      )}
    </CustomDialog>
  );
}

export default PickupsDialog;
